import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment
} from '@mui/material';
import { Add as AddIcon, Settings as SettingsIcon, Search as SearchIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from '../utils/axios';
import OCIADetails from './OCIADetails';
import OCIAForm from './OCIAForm';
import { useError } from '../contexts/ErrorContext';
import { useAuth } from '../contexts/AuthContext';
import { ociaFullName, formatPhoneNumber } from '../utils/ociaFields';

const OCIAList = () => {
  const [registrations, setRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [availableSchoolYears, setAvailableSchoolYears] = useState([]);
  const [currentSchoolYear, setCurrentSchoolYear] = useState('');
  const { showError } = useError();
  const { user } = useAuth();

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchRegistrations(selectedSchoolYear);
  }, [selectedSchoolYear]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/ocia/settings');
      if (response.data) {
        const years = response.data.availableSchoolYears || [];
        const current = response.data.currentSchoolYear || '';
        setAvailableSchoolYears(years);
        setCurrentSchoolYear(current);
        setSelectedSchoolYear(current || (years.length > 0 ? years[0] : ''));
      }
    } catch (error) {
      console.error('Error fetching OCIA settings:', error);
      fetchRegistrations('');
    }
  };

  const fetchRegistrations = async (schoolYear) => {
    try {
      const params = {};
      if (schoolYear) {
        params.schoolYear = schoolYear;
      }
      const response = await axios.get('/ocia', { params });
      if (response.data) {
        setRegistrations(response.data);
      }
    } catch (error) {
      setRegistrations([]);
      showError('Failed to fetch OCIA registrations');
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    const searchLower = searchTerm.toLowerCase();
    return (
      ociaFullName(reg).toLowerCase().includes(searchLower) ||
      (reg.email || '').toLowerCase().includes(searchLower) ||
      (reg.cellPhone || '').includes(searchTerm)
    );
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom component="div" sx={{ mb: 0 }}>
          OCIA Registrations
        </Typography>
        {user && user.isAdmin && (
          <IconButton component={Link} to="/ocia/settings" color="primary" aria-label="settings">
            <SettingsIcon />
          </IconButton>
        )}
      </Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel id="ocia-school-year-label">Year</InputLabel>
              <Select
                labelId="ocia-school-year-label"
                id="ocia-school-year-select"
                value={selectedSchoolYear}
                label="Year"
                onChange={(e) => setSelectedSchoolYear(e.target.value)}
              >
                {availableSchoolYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}{year === currentSchoolYear ? ' (Current)' : ''}
                  </MenuItem>
                ))}
                <MenuItem value="">All Years</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search name, email, phone"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsFormOpen(true)}
          >
            New Manual Registration
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Cell Phone</TableCell>
                <TableCell>Registration Date</TableCell>
                <TableCell>Payment Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRegistrations.map((registration) => (
                <TableRow
                  key={registration._id}
                  onClick={() => setSelectedRegistration(registration)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <TableCell>{ociaFullName(registration)}</TableCell>
                  <TableCell>{registration.email}</TableCell>
                  <TableCell>{registration.cellPhone ? formatPhoneNumber(registration.cellPhone) : ''}</TableCell>
                  <TableCell>{registration.registrationDate ? new Date(registration.registrationDate).toLocaleDateString() : ''}</TableCell>
                  <TableCell>
                    <Chip
                      label={registration.payment?.status ? registration.payment.status.charAt(0).toUpperCase() + registration.payment.status.slice(1) : 'Open'}
                      color={
                        registration.payment?.status === 'paid' ? 'success' :
                        registration.payment?.status === 'hardship' ? 'info' :
                        'warning'
                      }
                      variant="filled"
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {filteredRegistrations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No registrations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedRegistration && (
          <OCIADetails
            registration={selectedRegistration}
            open={Boolean(selectedRegistration)}
            onClose={() => setSelectedRegistration(null)}
            onUpdate={() => fetchRegistrations(selectedSchoolYear)}
          />
        )}

        <OCIAForm
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSuccess={() => fetchRegistrations(selectedSchoolYear)}
          schoolYear={selectedSchoolYear}
          currentSchoolYear={currentSchoolYear}
        />
      </Paper>
    </Container>
  );
};

export default OCIAList;
